"use client";

import { spacing, colors } from "@softmax/design-system/tokens.stylex";

import type { WikiLoadState, WikiRefreshState } from "./wiki-types";
import { useState } from "react";
import * as stylex from "@stylexjs/stylex";
import { styles } from "./wiki.styles";
import { Button, Alert, Spinner } from "@softmax/design-system";
import {
  ResourceFeedback,
  type ChartRefreshState,
} from "@softmax/web-ux/resource-feedback";

export function WikiResourceStatus<T>({
  state,
  label,
  onRefresh,
  refreshable = true,
}: Readonly<{
  state: WikiLoadState<T>;
  label: string;
  onRefresh(): void;
  refreshable?: boolean;
}>) {
  const [retried, setRetried] = useState(false);
  const pending =
    state.status === "loading" ||
    (state.status === "ready" && state.refresh.status === "loading");

  if (!refreshable) {
    return (
      <ResourceFeedback
        subject={label}
        onRefresh={onRefresh}
        state={
          state.status === "ready"
            ? {
                status: "ready",
                refresh: resourceRefresh(state.refresh),
              }
            : state
        }
      />
    );
  }

  return (
    <div {...stylex.props(localStyles.refresh)}>
      {(state.status !== "loading" || retried) && (
        <Button
          size="sm"
          variant={state.status === "ready" ? "quiet" : "secondary"}
          disabled={pending}
          focusableWhenDisabled
          onClick={() => {
            setRetried(true);
            onRefresh();
          }}
        >
          {state.status === "ready" ? "Refresh" : "Retry"} {label}
        </Button>
      )}
      <WikiRefreshStatus state={state} pending={pending} label={label} />
    </div>
  );
}

const localStyles = stylex.create({
  refresh: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: spacing.space8,
    marginBlock: spacing.space8,
  },
  errorText: { color: colors.danger },
});

function WikiRefreshStatus<T>({
  state,
  pending,
  label,
}: Readonly<{
  state: WikiLoadState<T>;
  pending: boolean;
  label: string;
}>) {
  return (
    <>
      {state.status === "error" ? (
        <Alert tone="danger" announcement="assertive">
          {state.message}
        </Alert>
      ) : (
        <output {...stylex.props(styles.smallMuted)}>
          {pending && (
            <>
              <Spinner />{" "}
              {state.status === "loading" ? `Loading ${label}…` : "Refreshing…"}
            </>
          )}
          {state.status === "ready" && state.refresh.status === "error" && (
            <span {...stylex.props(localStyles.errorText)}>
              {state.refresh.message}
            </span>
          )}
        </output>
      )}
    </>
  );
}

function resourceRefresh(
  refresh: WikiRefreshState,
): ChartRefreshState | undefined {
  if (refresh.status === "loading") return { status: "refreshing" };

  if (refresh.status === "error") return refresh;

  return undefined;
}
